import { apiJson } from '../api/client.js';
import { downloadPdfDocument } from '../components/PdfDocumentsPanel.jsx';
import { DEFAULT_PAGE_MARGINS_MM, normalizePageMargins } from './htmlToPdf.js';
import { preparePdfImport, PDF_IMPORT_LAYOUT_VERSION } from './pdfTextToHtml.js';

/**
 * One-time PDF → editable HTML import. Call only on first upload (or when
 * needs_text_import is still true). Persists editor_state on the server and
 * must not be re-run on later editor opens.
 *
 * @param {number|string} documentId
 * @param {{
 *   title?: string,
 *   onStatus?: (message: string) => void,
 * }} [options]
 */
export async function runPdfDocumentImport(documentId, options = {}) {
  const id = Number(documentId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('Invalid document id for PDF import.');
  }

  const onStatus = typeof options.onStatus === 'function' ? options.onStatus : () => {};
  const title = String(options.title || '').trim();

  onStatus('Reading uploaded PDF…');
  const blob = await downloadPdfDocument(id, 'admin', { source: true });

  onStatus('Extracting text, tables cues, and images…');
  const prepared = await preparePdfImport(blob, { maxPages: 15 });

  onStatus('Building a faithful editable document with AI (first upload only)…');
  const imported = await apiJson(`/api/admin/pdf-documents/${id}/import-html`, {
    method: 'POST',
    body: {
      title,
      force_ai: true,
      persist: true,
      pages: prepared.pages.map((p) => ({
        text: p.text,
        rich_text: p.richText || '',
        image: p.image,
      })),
      assets: (prepared.assets || []).map((a) => ({
        id: a.id,
        data_url: a.data_url,
        width: a.width,
        height: a.height,
      })),
    },
  });

  // Prefer server-persisted document when returned; otherwise PUT editor_state.
  if (imported.document?.id) {
    onStatus(imported.message || 'Document imported and saved.');
    return imported.document;
  }

  const nextState = {
    version: 2,
    mode: 'document',
    html: imported.html,
    needs_text_import: false,
    source: 'upload',
    imported_from_pdf: true,
    imported_layout_version: PDF_IMPORT_LAYOUT_VERSION,
    imported_pages: imported.page_count ?? prepared.pageCount,
    imported_has_text: imported.has_text ?? prepared.textLength >= 8,
    import_source: imported.source || null,
    page_margins: normalizePageMargins(DEFAULT_PAGE_MARGINS_MM),
  };

  const updated = await apiJson(`/api/admin/pdf-documents/${id}`, {
    method: 'PUT',
    body: { editor_state: nextState },
  });

  onStatus(imported.message || 'Document imported and saved.');
  return updated;
}
